import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, Glyphs} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import {ICollectionModel} from '../../model/appModel';
import collectionApi from '../../service/collectionApi';
import {StyledWarning, MinWidthButtonWrapper} from '../util/StyledElements';
import ShareToCollectionEmptyState from './ShareToCollectionEmptyState';
import '../../static/css/modal.less';
import {GroupRoleTypes} from '../../constants/Constants';
import {IWorkerState} from '../../model/workerState';
import styled from 'styled-components';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {collectionsStore, updateSidebarCollections} from '../../context/collectionsProvider';
import {mediaStore, updateMediaStateFromServer} from '../../context/mediaProvider';
import {AlertModalPortal, BasicModalPortal} from '../util/ModalPortalHelper';

const ModalSectionLabel = styled.h2`
   font-size: .875rem;
   font-weight: 600;
   margin: 0;
`;

export const ShareToCollectionModalBase: React.FC<IShareToCollectionModalProps & IStateMappedProps> = ({onClose, onCreateCollection, sidebarCollections, Hash, CollectionIds}) => {
   const {t} = useTranslation();
   const potentialMediaShareCollections = sidebarCollections?.filter(g => g.Role !== GroupRoleTypes.participant) ?? [];
   const [shareCollectionModalState, setShareCollectionModalState] = React.useState(
      {
         toAdd: [],
         toRemove: [],
         warnAboutCancel: false,
         isWorking: false,
         hasError: false
      } as IShareToCollectionModalState);

   const onSaveClick = async () => {
      setShareCollectionModalState({...shareCollectionModalState, isWorking: true, hasError: false});

      try {
         const addToCollectionPromises = shareCollectionModalState.toAdd.map(g => collectionApi.addToCollection(g.Id, Hash));
         const removeFromCollectionPromises = shareCollectionModalState.toRemove.map(g => collectionApi.removeFromCollection(g.Id, Hash));
         await Promise.all(addToCollectionPromises.concat(removeFromCollectionPromises));

         updateMediaStateFromServer();

         setShareCollectionModalState({...shareCollectionModalState, isWorking: false, hasError: false, toAdd: [], toRemove: []});
         onClose();
      } catch {
         setShareCollectionModalState({...shareCollectionModalState, isWorking: false, hasError: true});
         const errorMessage = document.getElementById('error-message');
         errorMessage.scrollIntoView({block: 'end', inline: 'nearest', behavior: 'smooth'});
      }
   };

   const fetchSidebarCollections = async () => {
      setShareCollectionModalState({...shareCollectionModalState, isWorking: true});
      await updateSidebarCollections();
      setShareCollectionModalState({...shareCollectionModalState, isWorking: false});
   };

   useEffect(() => {
      if (sidebarCollections?.length === 0) {
         void fetchSidebarCollections();
      }
   }, []);

   const onCancelClick = () => {
      setShareCollectionModalState({...shareCollectionModalState, warnAboutCancel: true});
   };

   const onCancelWarnClose = () => {
      setShareCollectionModalState({...shareCollectionModalState, warnAboutCancel: false});
   };

   const onCancelWarnConfirm = () => {
      setShareCollectionModalState({...shareCollectionModalState, warnAboutCancel: false});
      onClose();
   };

   const onCreateCollectionClick = async () => {
      if (shareCollectionModalState.toRemove.length !== 0 || shareCollectionModalState.toAdd.length !== 0) {
         await onSaveClick();
      }

      onCreateCollection && onCreateCollection();
   };

   const getCollectionToDisplay = (id: string, isShared: boolean) => {
      const getCollectionName = () => potentialMediaShareCollections.find(c => c.Id === id).Name;

      const removeFromCollection = () => {
         if (shareCollectionModalState.toAdd.findIndex(c => c.Id === id) !== -1) {
            const holdArray = shareCollectionModalState.toAdd.slice();
            holdArray.splice(holdArray.findIndex(c => c.Id === id), 1);
            setShareCollectionModalState({...shareCollectionModalState, toAdd: holdArray});
         } else if (shareCollectionModalState.toRemove.findIndex(c => c.Id === id) === -1) {
            setShareCollectionModalState({
               ...shareCollectionModalState,
               toRemove: shareCollectionModalState.toRemove.concat(potentialMediaShareCollections.find(c => c.Id === id))
            });
         }
      };

      const addToCollection = () => {
         if (shareCollectionModalState.toRemove.findIndex(c => c.Id === id) !== -1) {
            const holdArray = shareCollectionModalState.toRemove.slice();
            holdArray.splice(holdArray.findIndex(c => c.Id === id), 1);
            setShareCollectionModalState({...shareCollectionModalState, toRemove: holdArray});
         } else if (shareCollectionModalState.toAdd.findIndex(c => c.Id === id) === -1) {
            setShareCollectionModalState({
               ...shareCollectionModalState,
               toAdd: shareCollectionModalState.toAdd.concat(potentialMediaShareCollections.find(c => c.Id === id))
            });
         }
      };

      const collection = potentialMediaShareCollections.find(c => c.Id === id);
      const userRole = collection?.Role;
      const isOwner = userRole === GroupRoleTypes.manager;
      return (
         <li
            className="collection"
            key={id}
         >
            <span id={`collection-name-${id}`}>
               {getCollectionName()}
            </span>
            {isShared && isOwner ? (
               <ControlButton
                  title={t('collections.share.remove')}
                  buttonType={TscButtonType.floating}
                  glyph={<Glyphs.CircleRemove16x16 />}
                  onClick={removeFromCollection}
                  testId="remove-collection-button"
                  aria-labelledby={`collection-name-${id}`}
               />
            ) : (
               !isShared && (
                  <ControlButton
                     title={t('collections.share.add')}
                     buttonType={TscButtonType.floating}
                     glyph={<Glyphs.CircleAdd16x16 />}
                     onClick={addToCollection}
                     testId="add-collection-button"
                     aria-labelledby={`collection-name-${id}`}
                  />
               ))}
         </li>
      );
   };

   const isMediaSharedToCollection = (c: ICollectionModel) => CollectionIds.some(h => h === c.Id);

   const isCollectionInPendingAddState = (c: ICollectionModel) => shareCollectionModalState.toAdd.some(g => g.Id === c.Id);

   const isCollectionInPendingRemoveState = (c: ICollectionModel) => shareCollectionModalState.toRemove.some(g => g.Id === c.Id);

   const collectionsToShareWith = potentialMediaShareCollections
      .filter(isMediaSharedToCollection)
      .filter(g => !isCollectionInPendingRemoveState(g))
      .concat(shareCollectionModalState.toAdd) || [];

   const sharedWithElement = collectionsToShareWith?.length ?
      collectionsToShareWith.map(g => getCollectionToDisplay(g.Id, true)) : (
         <div className="empty-state-text">
            {t('collections.share.notShared')}
         </div>
      );

   const collectionsAvailableForSharing = potentialMediaShareCollections
      .filter(g => !isMediaSharedToCollection(g))
      .filter(g => !isCollectionInPendingAddState(g))
      .concat(shareCollectionModalState.toRemove) || [];

   const myCollectionsElement = collectionsAvailableForSharing?.length ?
      collectionsAvailableForSharing.map(g => getCollectionToDisplay(g.Id, false)) : (
         <div className="empty-state-text">
            {t('collections.share.none')}
         </div>
      );

   const pendingChanges = shareCollectionModalState.toRemove.length !== 0 || shareCollectionModalState.toAdd.length !== 0;

   const displayedButtons = !pendingChanges ?
      (
         <MinWidthButtonWrapper>
            <ControlButton
               label={t('general.done')}
               buttonType={TscButtonType.primary}
               onClick={onClose}
               testId="done-button"
            />
         </MinWidthButtonWrapper>
      ) :
      (
         <>
            <MinWidthButtonWrapper>
               <ControlButton
                  label={t('general.cancel')}
                  buttonType={TscButtonType.secondary}
                  onClick={onCancelClick}
                  testId="cancel-button"
               />
               <ControlButton
                  label={t('general.save')}
                  buttonType={TscButtonType.primary}
                  onClick={onSaveClick}
                  testId="save-button"
               />
            </MinWidthButtonWrapper>
         </>
      );

   const hideEmptyState = shareCollectionModalState.isWorking || shareCollectionModalState.hasError || (collectionsAvailableForSharing.length > 0 || collectionsToShareWith.length > 0);

   const ModalContent = (
      <>
         <div className="share-to-collection-field form-field">
            <ModalSectionLabel>
               {t('collections.share.sharedWith')}
            </ModalSectionLabel>
         </div>
         <ul className="share-collection-section">
            {sharedWithElement}
         </ul>
         <div className="share-to-collection-field form-field">
            <ModalSectionLabel>
               {t('collections.mine')}
            </ModalSectionLabel>
            <MinWidthButtonWrapper>
               <ControlButton
                  label={t('collections.create.new')}
                  buttonType={TscButtonType.secondary}
                  onClick={onCreateCollectionClick}
                  testId="create-collection-button"
               />
            </MinWidthButtonWrapper>
         </div>
         <ul className="share-collection-section">
            {myCollectionsElement}
         </ul>
         <div className="button-group">
            {displayedButtons}
         </div>
         {shareCollectionModalState.warnAboutCancel &&
            (
               <AlertModalPortal
                  title={t('general.unsavedChanges')}
                  testId="manage-reviewers-warn"
                  onClose={onCancelWarnClose}
                  closeContent={t('general.close')}
                  onConfirm={onCancelWarnConfirm}
                  confirmContent={t('general.confirm')}
                  confirmButtonType={TscButtonType.destructive}
                  onDismiss={onCancelWarnClose}
                  dismissContent={t('general.close')}
                  dismissButtonType={TscButtonType.secondary}
                  modalWidth="20rem"
               >
                  <div className="manage-reviewers-warn">
                     {t('general.unsavedChangesDescription')}
                  </div>
               </AlertModalPortal>
            )
         }
      </>
   );

   return (
      <BasicModalPortal
         testId="share-to-collection-modal"
         title={t('collections.share.header')}
         width={cssConstants.defaultModalWidth}
         onClose={onClose}
         isLoading={shareCollectionModalState.isWorking}
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               {shareCollectionModalState.hasError && (
                  <StyledWarning id="error-message">
                     {t('general.errorText')}
                  </StyledWarning>
               )}
               {hideEmptyState ? ModalContent : <ShareToCollectionEmptyState onCreateCollection={onCreateCollection} />}
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IShareToCollectionModalProps {
   onClose(): void;
   onCreateCollection?(): void;
}

export interface IShareToCollectionModalState extends IWorkerState {
   toAdd: ICollectionModel[];
   toRemove: ICollectionModel[];
   warnAboutCancel: boolean;
}

export interface IStateMappedProps {
   sidebarCollections: ICollectionModel[];
   Hash: string;
   CollectionIds: string[];
}

const mapStatesToProps = (
   {onClose, onCreateCollection}: IShareToCollectionModalProps,
   {sidebarCollections}: Partial<IStateMappedProps>,
   {Hash, CollectionIds}: Partial<IStateMappedProps>
): IStateMappedProps & IShareToCollectionModalProps => ({onClose, onCreateCollection, sidebarCollections, Hash, CollectionIds});

export default withMemoizedContexts(mapStatesToProps, collectionsStore, mediaStore)(ShareToCollectionModalBase);
